/**
 * Teacher Metrics Aggregate (Phase 9 / Module 9) validation contracts.
 *
 * Mirrors the schema in `packages/db/src/schema/performance.ts` and the
 * runtime helpers in `apps/api/src/lib/performance/*`. Used by route
 * handlers (input validation), the nightly snapshot cron (config shape),
 * and any downstream consumers that need to type the snapshot payload.
 *
 * Locked v1:
 *   - Seven metric variables, identified by `performanceVariableKeys`
 *     (reporting_discipline, the seventh, is off by default).
 *   - Default weights sum to 1.00; HR-edited weights re-validated by the API.
 *   - Banding thresholds `{ top, medium }` with `0 < medium < top <= 100`.
 *   - Sliding window length (`periodDays`) bounded 30–180.
 *
 * Spec: docs/deep-research-report4.md §"TMA Scoring Formula".
 */
import { z } from 'zod';
import { dateStringSchema, idSchema, paginationSchema } from './common.js';
import { probationVerdictSchema } from './hiring.js';

// ----- Variables ----------------------------------------------------------

/**
 * The canonical ordered list of metric variables that contribute to the
 * composite TMA score. The order is the order the breakdown is reported
 * in the API so UIs can render consistent rows without bespoke sorting.
 */
export const performanceVariableKeys = [
  'attendance_rate',
  'student_exam_avg',
  'fee_collection_rate',
  'quiz_participation_rate',
  'referral_count_normalized',
  'parent_feedback_avg',
  'reporting_discipline',
] as const;
export type PerformanceVariableKey = (typeof performanceVariableKeys)[number];

export const performanceBandValues = [
  'top',
  'medium',
  'average',
  'warming_up',
  'hidden',
] as const;
export type PerformanceBandValue = (typeof performanceBandValues)[number];

// ----- Config -------------------------------------------------------------

/**
 * Numeric weights per variable, bounded 0..1 inclusive. Values are kept as
 * floats; the route handler enforces the sum-to-1 invariant in addition to
 * per-key bounds.
 */
const weightValueSchema = z.number().min(0).max(1);

export const performanceWeightsSchema = z
  .object({
    attendance_rate: weightValueSchema,
    student_exam_avg: weightValueSchema,
    fee_collection_rate: weightValueSchema,
    quiz_participation_rate: weightValueSchema,
    referral_count_normalized: weightValueSchema,
    parent_feedback_avg: weightValueSchema,
    /**
     * Teacher reports on time (added with the reports module). Configs
     * saved before it existed have no key and read as 0.
     */
    reporting_discipline: weightValueSchema.default(0),
  })
  .strict();
export type PerformanceWeights = z.infer<typeof performanceWeightsSchema>;

export const performanceIncludeFlagsSchema = z
  .object({
    attendance_rate: z.boolean(),
    student_exam_avg: z.boolean(),
    fee_collection_rate: z.boolean(),
    quiz_participation_rate: z.boolean(),
    referral_count_normalized: z.boolean(),
    parent_feedback_avg: z.boolean(),
    /** Off unless HR turns it on — older configs have no key. */
    reporting_discipline: z.boolean().default(false),
  })
  .strict();
export type PerformanceIncludeFlags = z.infer<typeof performanceIncludeFlagsSchema>;

/**
 * Banding thresholds. The route validates the additional ordering
 * invariant (`top > medium > 0`) on PATCH /config so this schema can be
 * reused for partial updates without re-stating the rule.
 */
export const performanceThresholdsSchema = z
  .object({
    top: z.number().int().min(1).max(100),
    medium: z.number().int().min(1).max(99),
  })
  .strict();
export type PerformanceThresholds = z.infer<typeof performanceThresholdsSchema>;

/** Default weights mandated by the v4 brief. Sum: 1.00. */
export const performanceDefaultWeights: PerformanceWeights = {
  attendance_rate: 0.2,
  student_exam_avg: 0.25,
  fee_collection_rate: 0.15,
  quiz_participation_rate: 0.1,
  referral_count_normalized: 0.1,
  parent_feedback_avg: 0.2,
  reporting_discipline: 0,
};

/**
 * Default include flags — every variable participates except
 * reporting_discipline, which HR opts into once teacher reports are live.
 */
export const performanceDefaultIncludeFlags: PerformanceIncludeFlags = {
  attendance_rate: true,
  student_exam_avg: true,
  fee_collection_rate: true,
  quiz_participation_rate: true,
  referral_count_normalized: true,
  parent_feedback_avg: true,
  reporting_discipline: false,
};

export const performanceDefaultThresholds: PerformanceThresholds = {
  top: 80,
  medium: 65,
};

/**
 * PATCH /api/performance/config body. Every field is optional — HR can
 * tune one knob at a time. The handler stamps `effective_from=now()` and
 * writes a new history row. Validation of cross-field invariants
 * (weights sum, threshold ordering) lives in the route.
 */
export const performanceConfigUpdateSchema = z
  .object({
    periodDays: z.number().int().min(30).max(180).optional(),
    weights: performanceWeightsSchema.optional(),
    thresholds: performanceThresholdsSchema.optional(),
    referralSaturationCap: z.number().int().min(1).max(100).optional(),
    includeFlags: performanceIncludeFlagsSchema.optional(),
    minActiveDaysForBand: z.number().int().min(0).max(180).optional(),
    warmingUpTenureDays: z.number().int().min(0).max(180).optional(),
    feeGracePeriodDays: z.number().int().min(0).max(30).optional(),
    notes: z.string().trim().min(1).max(1000).nullable().optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, {
    message: 'Provide at least one field to update',
  });
export type PerformanceConfigUpdateInput = z.infer<
  typeof performanceConfigUpdateSchema
>;

// ----- Snapshot reads -----------------------------------------------------

export const performanceTrendQuerySchema = z
  .object({
    days: z.coerce.number().int().min(1).max(90).default(30),
  })
  .strict();
export type PerformanceTrendQuery = z.infer<typeof performanceTrendQuerySchema>;

export const performanceLeaderboardQuerySchema = paginationSchema.extend({
  snapshotDate: dateStringSchema.optional(),
  band: z.enum(performanceBandValues).optional(),
  sortBy: z.enum(['score', 'teacherName']).default('score'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
  includeWarmingUp: z.coerce.boolean().default(false),
  includeHidden: z.coerce.boolean().default(false),
});
export type PerformanceLeaderboardQuery = z.infer<
  typeof performanceLeaderboardQuerySchema
>;

export const performanceExportQuerySchema = z
  .object({
    snapshotDate: dateStringSchema.optional(),
    includeWarmingUp: z.coerce.boolean().default(false),
    includeHidden: z.coerce.boolean().default(false),
  })
  .strict();
export type PerformanceExportQuery = z.infer<typeof performanceExportQuerySchema>;

// ----- Recompute ----------------------------------------------------------

/**
 * POST /api/performance/snapshots/recompute body. All fields optional:
 *   - no fields            → recompute every active teacher for today
 *   - teacherIds only      → recompute those teachers for today
 *   - snapshotDate only    → recompute every active teacher for that date
 *   - both                 → recompute the specified teachers for that date
 */
export const performanceRecomputeSchema = z
  .object({
    snapshotDate: dateStringSchema.optional(),
    teacherIds: z.array(idSchema).min(1).max(500).optional(),
  })
  .strict();
export type PerformanceRecomputeInput = z.infer<typeof performanceRecomputeSchema>;

// ----- Notify parents -----------------------------------------------------

export const performanceAudienceKindSchema = z.enum([
  'overdue_fees',
  'attendance_low',
  'low_quiz_participation',
]);
export type PerformanceAudienceKind = z.infer<
  typeof performanceAudienceKindSchema
>;

export const performanceNotifyParentsSchema = z
  .object({
    audienceKind: performanceAudienceKindSchema,
    subject: z.string().trim().min(2).max(160).optional(),
    messageText: z.string().trim().min(2).max(2000),
  })
  .strict();
export type PerformanceNotifyParentsInput = z.infer<
  typeof performanceNotifyParentsSchema
>;

// ----- Publish report -----------------------------------------------------

/**
 * POST /api/performance/teachers/:id/reports/:periodKey/publish body.
 *
 * Marks a teacher's snapshot(s) for the given period as published —
 * surfaces the report on the teacher profile (and downstream consumers).
 * Notes are HR's optional caption shown alongside the published banner.
 *
 * Publication state is recorded as an `audit_logs` row (action
 * `performance.report_published`, `target_type='performance_snapshot'`,
 * `target_id=<teacherId>`, metadata `{ periodKey, notes? }`). Mirrors the
 * HR-comments convention used elsewhere in this validator's route — no
 * schema migration required.
 */
export const publishReportSchema = z
  .object({
    notes: z.string().trim().max(4000).optional(),
  })
  .strict();
export type PublishReportInput = z.infer<typeof publishReportSchema>;

// ----- Response shapes (informational; not server-enforced) --------------

/**
 * Per-variable breakdown emitted on every snapshot. Stored in
 * `performance_snapshots.metrics_json`. All numeric fields are finite;
 * `raw` is in the variable's natural unit (0..100 for ratios, integer
 * count for referrals, 1..5 mean for parent_feedback before
 * normalisation).
 */
export const performanceMetricBreakdownSchema = z.object({
  raw: z.number(),
  normalized: z.number().min(0).max(100),
  weighted: z.number().min(0).max(100),
  weight: z.number().min(0).max(1),
  effectiveWeight: z.number().min(0).max(1),
  dataPresent: z.boolean(),
  included: z.boolean(),
  /**
   * Free-form note explaining why a variable's effective weight differs
   * from its configured weight (e.g. `excluded_by_flag`,
   * `no_data_dropped`, `parent_feedback_carryforward_50_pct`).
   */
  note: z.string().nullable(),
  /**
   * Per-variable raw-source summary (counts, sums) exposed by the
   * breakdown endpoint. Shape is variable-specific; consumers narrow at
   * read time. The schema accepts `unknown` to keep the breakdown
   * extensible without round-tripping every variable's bespoke type
   * through the validator.
   */
  summary: z.unknown(),
});
export type PerformanceMetricBreakdown = z.infer<
  typeof performanceMetricBreakdownSchema
>;

export const performanceMetricsJsonSchema = z.object({
  attendance_rate: performanceMetricBreakdownSchema,
  student_exam_avg: performanceMetricBreakdownSchema,
  fee_collection_rate: performanceMetricBreakdownSchema,
  quiz_participation_rate: performanceMetricBreakdownSchema,
  referral_count_normalized: performanceMetricBreakdownSchema,
  parent_feedback_avg: performanceMetricBreakdownSchema,
  /** Absent on snapshots taken before the variable existed. */
  reporting_discipline: performanceMetricBreakdownSchema.optional(),
});
export type PerformanceMetricsJson = z.infer<typeof performanceMetricsJsonSchema>;

export const performanceSnapshotResponseSchema = z.object({
  teacherId: z.string(),
  teacherName: z.string().nullable(),
  snapshotDate: z.string(),
  periodStart: z.string(),
  periodEnd: z.string(),
  periodDays: z.number().int(),
  tenureDays: z.number().int(),
  activeTeachingDays: z.number().int(),
  score: z.number().int().min(0).max(100),
  band: z.enum(performanceBandValues),
  metrics: performanceMetricsJsonSchema,
  weightsEffective: performanceWeightsSchema,
  parentFeedbackCarryforwardApplied: z.boolean(),
  configId: z.string(),
});
export type PerformanceSnapshotResponse = z.infer<
  typeof performanceSnapshotResponseSchema
>;

// ----- Probation history (M-6) -------------------------------------------

/**
 * One probation week as surfaced alongside the TMA snapshot/breakdown.
 * `weekNo` is 1..12 under the current policy but the API does not clamp the
 * upper bound to the snapshot contract (probation may be extended per the
 * schema comment), so the max here tolerates beyond 12 defensively.
 *
 * `verdict` reuses the canonical `probationVerdictSchema` from `./hiring.js`
 * so the TMA surface and the hiring write-path share one source of truth.
 */
export const probationWeekSummarySchema = z.object({
  weekNo: z.number().int().min(1),
  periodStart: z.string(),
  periodEnd: z.string(),
  verdict: probationVerdictSchema,
  overallScore: z.number().int().min(0).max(100).nullable(),
});
export type ProbationWeekSummary = z.infer<typeof probationWeekSummarySchema>;

/**
 * Aggregated probation history attached as a sibling field on the TMA
 * snapshot and breakdown responses. `null` (not an empty object) when the
 * teacher has no recorded reviews. Does not affect the numeric TMA score.
 */
export const probationSummarySchema = z.object({
  reviewsRecorded: z.number().int().min(0),
  weeksCompleted: z.number().int().min(0),
  averageScore: z.number().int().min(0).max(100).nullable(),
  worstVerdict: probationVerdictSchema,
  weeks: z.array(probationWeekSummarySchema),
});
export type ProbationSummary = z.infer<typeof probationSummarySchema>;
